import {z} from "../../../../../lib";
import {OperacionCLGDetalleFields, OperacionCLGFields,
    OperacionCLGLimiteAprobadoFields, OperacionCLGLimitesAprobadosPaisFields, OperacionCLGSectorComercialFields} from "./fields";
import {EntityWithId, EntityWithIdFields, schemaEntityWithId} from "../../../../../types";

export const schemaOperacionCLG = z
    .object({
        [OperacionCLGFields.CodPais]: z.number(),
        [OperacionCLGFields.NombrePais]: z.string(),
        [OperacionCLGFields.CodOperacion]: z.number(),
        [OperacionCLGFields.CodInstitutionalSales]: z.number(),
        [OperacionCLGFields.NombreInstitutionalSales]: z.string(),
        [OperacionCLGFields.FechaAprobacion]: z.coerce.date(),
        [OperacionCLGFields.FechaVencimiento]: z.coerce.date(),
        [OperacionCLGFields.FechaAlta]: z.coerce.date().nullable(),
        [OperacionCLGFields.IdUsuarioAlta]: z.number(),
        [OperacionCLGFields.NombreUsuarioAlta]: z.string(),
        [OperacionCLGFields.ApellidoUsuarioAlta]: z.string(),
        [OperacionCLGFields.MailUsuarioAlta]: z.string(),
        [OperacionCLGFields.IdMensaje]: z.number(),
        [OperacionCLGFields.PermiteEditar]: z.boolean(),
        [OperacionCLGFields.PermiteEliminar]: z.boolean(),
        [OperacionCLGFields.EstadoFinalizado]: z.boolean(),


    })
    .extend({
        [EntityWithIdFields.Id]: z.number()
    })

export type OperacionCLG = z.infer<typeof schemaOperacionCLG>

const schemaFormCompartido = {
    [OperacionCLGFields.FechaAprobacion]: z.date(),
    [OperacionCLGFields.FechaVencimiento]: z.date(),
    [OperacionCLGFields.CodInstitutionalSales]: z.number()
}

export const schemaFormOperacionCLG = z.object(schemaFormCompartido)
    .refine(
        schema =>
            (schema[OperacionCLGFields.FechaVencimiento]?.getTime() ?? 0) >=
            schema[OperacionCLGFields.FechaAprobacion].getTime(),
        {
            message: 'La fecha de vencimiento debe ser mayor a la fecha de emisión.',
            path: [OperacionCLGFields.FechaVencimiento]
        }
    );

export type FormOperacionCLG = z.infer<typeof schemaFormOperacionCLG>

export const CLGDetalleInsertSchema = z.object({
    [OperacionCLGDetalleFields.IdOperacion]: z.number().nullable(),
    [OperacionCLGDetalleFields.IdCabecera]: z.number().nullable(),
    [OperacionCLGDetalleFields.Aforo]: z.number()
      .or(z.string().min(1, 'Aforo requerido')),
    [OperacionCLGDetalleFields.CodSeccion]: z.number(),
    [OperacionCLGDetalleFields.CodEspecie]: z.number(),
    [OperacionCLGDetalleFields.Especie]: z.string(),
})

export type CLGDetalle_InsertDTO = z.infer<typeof CLGDetalleInsertSchema>

export const schemaFormNuevaOperacionCLG = z.object({
    ...schemaFormCompartido,
    [OperacionCLGFields.ListaDetalles]: z.array(
        CLGDetalleInsertSchema
    ),
}).refine(
    schema =>
        (schema[OperacionCLGFields.FechaVencimiento]?.getTime() ?? 0) >=
        schema[OperacionCLGFields.FechaAprobacion].getTime(),
    {
        message: 'La fecha de vencimiento debe ser mayor a la fecha de emisión.',
        path: [OperacionCLGFields.FechaVencimiento]
    }
);

export type FormNuevaOperacionCLG = z.infer<typeof schemaFormNuevaOperacionCLG>

export const schemaCLGDetalle = z
    .object({
        [OperacionCLGDetalleFields.IdOperacion]: z.number(),
        [OperacionCLGDetalleFields.IdCabecera]: z.number().nullable(),
        [OperacionCLGDetalleFields.Aforo]: z.string(),
        [OperacionCLGDetalleFields.CodSeccion]: z.number(),
        [OperacionCLGDetalleFields.Sector]: z.string(),
        [OperacionCLGDetalleFields.CodEspecie]: z.number(),
        [OperacionCLGDetalleFields.Especie]: z.string(),
        [OperacionCLGDetalleFields.OrdenCabecera]: z.number(),
        [OperacionCLGDetalleFields.OrdenSeccion]: z.number(),
        [OperacionCLGDetalleFields.OrdenEspecie]: z.number(),
        [OperacionCLGDetalleFields.PermiteModificar]: z.boolean()
    })
    .merge(schemaEntityWithId)

export type CLGDetalle = EntityWithId<number> & {
    [OperacionCLGDetalleFields.IdOperacion]: number;
    [OperacionCLGDetalleFields.IdCabecera]: number | null;
    [OperacionCLGDetalleFields.Aforo]: string;
    [OperacionCLGDetalleFields.CodSeccion]: number;
    [OperacionCLGDetalleFields.Sector]: string;
    [OperacionCLGDetalleFields.CodEspecie]: number;
    [OperacionCLGDetalleFields.Especie]: string;
    [OperacionCLGDetalleFields.OrdenCabecera]: number;
    [OperacionCLGDetalleFields.OrdenSeccion]: number;
    [OperacionCLGDetalleFields.OrdenEspecie]: number;
    [OperacionCLGDetalleFields.PermiteModificar]: boolean;
};

export const schemaCLGLimiteAprobado = schemaCLGDetalle.extend({
    [OperacionCLGLimitesAprobadosPaisFields.UsuarioAlta]: z.string(),
    [OperacionCLGLimitesAprobadosPaisFields.FechaAlta]: z.coerce.date(),
    [OperacionCLGLimitesAprobadosPaisFields.IdMensaje]: z.number(),

    [OperacionCLGLimiteAprobadoFields.FechaVencimiento]: z.coerce.date(),
    [OperacionCLGLimiteAprobadoFields.FechaAprobacion]: z.coerce.date().nullable(),
    [OperacionCLGLimiteAprobadoFields.CodSectorComercial]: z.number(),
    [OperacionCLGLimiteAprobadoFields.NombreSectorComercial]: z.string(),
});

export type CLGLimiteAprobado = CLGDetalle & z.infer<typeof schemaCLGLimiteAprobado>;

export const schemaCLGSectorComercial = z.object({
    [OperacionCLGSectorComercialFields.CodSectorComercial]: z.number(),
    [OperacionCLGSectorComercialFields.NombreSectorComercial]: z.string(),
    [OperacionCLGSectorComercialFields.FechaVencimiento]: z.coerce.date(),
    [OperacionCLGSectorComercialFields.FechaAprobacion]: z.coerce.date(),
    [OperacionCLGLimitesAprobadosPaisFields.IdMensaje]: z.number(),
    [OperacionCLGSectorComercialFields.ListaLimites]: z.array(schemaCLGLimiteAprobado),
});

export type OperacionCLGLimitesSectorComercial = z.infer<typeof schemaCLGSectorComercial>;

export const schemaCLGLimitesAprobadosPais = z.object({
    [OperacionCLGLimitesAprobadosPaisFields.UsuarioAlta]: z.string(),
    [OperacionCLGLimitesAprobadosPaisFields.FechaAlta]: z.coerce.date(),
    [OperacionCLGLimitesAprobadosPaisFields.IdMensaje]: z.number(),
    [OperacionCLGFields.CodPais]: z.number(),
    [OperacionCLGFields.NombrePais]: z.string(),

    [OperacionCLGLimitesAprobadosPaisFields.ListaSectores]: z.array(schemaCLGSectorComercial),
});

export type CLGLimitesAprobadosPais = z.infer<typeof schemaCLGLimitesAprobadosPais>;

export const schemaListaCLGLimitesAprobadosPais = z.array(schemaCLGLimitesAprobadosPais);


